import { ChangeEvent, DragEvent } from 'react';
import imageCompression from 'browser-image-compression';
import { openPopup } from 'src/store/common/common.slice';
import { stopMediaLoader } from 'src/store/socialMedia/socialMedia.slice';
import { useAppDispatch } from 'src/store/hooks';
import { TMediaType } from './useUploadMedia.types';
import { hideProgress, showProgress, updateProgress } from 'src/store/common/progress.slice';

const compressVideo = (file: File, quality = 0.6): Promise<File> => {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return reject(new Error('Canvas error'));

    video.onloadedmetadata = () => {
      let { videoWidth, videoHeight } = video;
      const maxWidth = 1280;
      const maxHeight = 720;
      const aspectRatio = videoWidth / videoHeight;

      if (videoWidth > maxWidth || videoHeight > maxHeight) {
        if (aspectRatio > 1) {
          videoWidth = maxWidth;
          videoHeight = maxWidth / aspectRatio;
        } else {
          videoHeight = maxHeight;
          videoWidth = maxHeight * aspectRatio;
        }
      }

      canvas.width = videoWidth;
      canvas.height = videoHeight;

      const stream = canvas.captureStream(30);
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'video/webm;codecs=vp8',
        videoBitsPerSecond: quality * 1_000_000
      });

      const chunks: Blob[] = [];
      mediaRecorder.ondataavailable = (e) => e.data.size && chunks.push(e.data);

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunks, { type: 'video/webm' });
        const compressed = new File([blob], file.name.replace(/\.[^/.]+$/, '.webm'), {
          type: 'video/webm'
        });
        resolve(compressed);
      };

      mediaRecorder.onerror = reject;

      const drawFrame = () => {
        if (!video.paused && !video.ended) {
          ctx.drawImage(video, 0, 0, videoWidth, videoHeight);
          requestAnimationFrame(drawFrame);
        } else {
          mediaRecorder.stop();
        }
      };

      video.play().then(() => {
        mediaRecorder.start();
        drawFrame();
      });
    };

    video.onerror = reject;
    video.src = URL.createObjectURL(file);
  });
};

const useUploadMedia = () => {
  const dispatch = useAppDispatch();

  const handleUploadMedia = async ({
    e,
    onLoad,
    mediaType,
    dragDrop = false
  }: {
    e: ChangeEvent<HTMLInputElement> | DragEvent<HTMLDivElement>;
    onLoad: (
      data: string | ArrayBuffer | null,
      fileType: string,
      mediaType: TMediaType,
      name: string,
      size: number
    ) => void;
    mediaType: TMediaType;
    dragDrop?: boolean;
  }) => {
    const files = dragDrop
      ? (e as DragEvent<HTMLDivElement>).dataTransfer.files
      : (e as ChangeEvent<HTMLInputElement>).target.files;

    if (!files) return;

    dispatch(showProgress());

    for (let i = 0; i < files.length; i++) {
      let file = files[i];
      const { name, type } = file;
      const fileType = name.split('.').pop()?.toLowerCase() || '';

      try {
        // Always compress image
        if (type.startsWith('image/')) {
          file = await imageCompression(file, {
            maxSizeMB: 0.5,
            maxWidthOrHeight: 1024,
            useWebWorker: true,
            onProgress: (percent) => dispatch(updateProgress(percent))
          })
        } else if (type.startsWith('video/')) {
          dispatch(updateProgress(5)); // Initial progress
          file = await compressVideo(file, 0.6);
          dispatch(updateProgress(90)); // Near end
        }

        const reader = new FileReader();
        reader.readAsArrayBuffer(file);
        reader.onload = () => {
          onLoad(reader.result, fileType, mediaType, name, file.size);
          dispatch(updateProgress(100));
          setTimeout(() => dispatch(hideProgress()), 500);
        };
      } catch (err) {
        dispatch(openPopup({ popupMessage: 'Compression failed.', popupType: 'error' }));
        dispatch(stopMediaLoader());
        dispatch(hideProgress());
      }
    }

    const element = e.target as HTMLInputElement;
    if (element && element.value) element.value = '';
  };

  return { handleUploadMedia };
};

export default useUploadMedia;
